import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  CALLSECT_META_DESCRIPTION,
  CALLSECT_SEO_TITLE,
  CALLSECT_SOCIAL_DESCRIPTION,
  CALLSECT_SOCIAL_IMAGE_ALT,
  CALLSECT_SOCIAL_IMAGE_PATH,
  CALLSECT_SOCIAL_TITLE,
} from "@/lib/positioning";

const SITE_URL = "https://callsect.com";
const SOCIAL_IMAGE = `${SITE_URL}${CALLSECT_SOCIAL_IMAGE_PATH}`;
const DEFAULT_DESCRIPTION = CALLSECT_META_DESCRIPTION;

const PUBLIC_ROUTES = {
  "/": {
    title: CALLSECT_SEO_TITLE,
    socialTitle: CALLSECT_SOCIAL_TITLE,
    description: DEFAULT_DESCRIPTION,
    socialDescription: CALLSECT_SOCIAL_DESCRIPTION,
    indexable: true,
    schema: true,
  },
  "/sales-call-review": {
    title: "Sales Call Review: What Changed the Outcome | Callsect",
    socialTitle: "Sales call review that goes beyond a summary",
    description:
      "Learn how to review sales calls using evidence, coaching, relationship context, and next-call preparation—not just transcripts or talk-time scores.",
    indexable: true,
  },
  "/sales-call-analysis-software": {
    title: "Sales Call Analysis Software Beyond Summaries | Callsect",
    socialTitle: "Sales call analysis software that carries the reasoning forward",
    description:
      "Sales call analysis software should connect evidence to interpretation, coaching, next moves, and relationship context—not stop at transcripts and scorecards.",
    indexable: true,
  },
  "/conversation-intelligence-vs-call-summarization": {
    title: "Conversation Intelligence vs Call Summarization | Callsect",
    socialTitle: "Conversation intelligence is more than a call summary",
    description:
      "Conversation intelligence should interpret what changed, why it matters, what to coach, and what happens next—not just compress a call into notes.",
    indexable: true,
  },
  "/prepare-for-next-customer-call": {
    title: "How to Prepare for Your Next Customer Call | Callsect",
    socialTitle: "Prepare the next customer call from the previous conversation",
    description:
      "Learn how to prepare for the next customer call using previous-conversation evidence, relationship context, coaching, open loops, and a concrete next-call objective.",
    indexable: true,
  },
  "/crm-notes-after-sales-call": {
    title: "CRM Notes After a Sales Call: What to Capture | Callsect",
    socialTitle: "Write CRM notes that preserve what changed in the call",
    description:
      "Learn what to capture in CRM notes after a sales call: blockers, stakeholders, commitments, relationship changes, and a clear next step without rewriting the transcript.",
    indexable: true,
  },
  "/sales-call-debrief-template": {
    title: "Sales Call Debrief Template: Evidence to Next Move | Callsect",
    socialTitle: "A sales call debrief template built around evidence and action",
    description:
      "Use a practical sales call debrief template to capture evidence, what changed, a better approach, and the next move instead of writing another recap.",
    indexable: true,
  },
  "/follow-up-email-from-sales-call-transcript": {
    title: "Follow-up Email From a Sales Call Transcript | Callsect",
    socialTitle: "Turn a sales call transcript into a useful follow-up email",
    description:
      "Turn a sales call transcript into a concise follow-up email by extracting the outcome, commitments, open loops, and relationship context that matter next.",
    indexable: true,
  },
  "/self-coaching-sales-calls": {
    title: "How to Self-Coach Your Sales Calls | Callsect",
    socialTitle: "Self-coach sales calls from real customer moments",
    description:
      "Learn how to self-coach sales calls using evidence, interpretation, a better approach, example wording, and lessons you can carry into the next conversation.",
    indexable: true,
  },
  "/renewal-call-preparation": {
    title: "How to Prepare for a Renewal Call | Callsect",
    socialTitle: "Prepare renewal calls from the customer history",
    description:
      "Prepare for a renewal call using conversation history, unresolved risk, stakeholders, commitments, and a concrete renewal objective instead of starting from a blank checklist.",
    indexable: true,
  },
  "/about": {
    title: "About Callsect | Built from Real Customer Work",
    socialTitle: "Built from real conversations. Refined through real work.",
    description:
      "Callsect is an independent product built and continuously refined alongside real account-management work and everyday customer conversations.",
    indexable: true,
  },
  "/product/relationship-memory": {
    title: "Customer Relationship Memory Across Calls | Callsect",
    socialTitle: "Keep every customer relationship moving.",
    description:
      "Callsect relationship memory carries stakeholders, commitments, objections, risks, and what changed across customer calls so the next conversation does not start from zero.",
    indexable: true,
  },
  "/product/crm-notes": {
    title: "CRM Notes & Follow-up Emails in Your Writing Style | Callsect",
    socialTitle: "Notes and follow-ups that still sound like you",
    description:
      "Turn customer calls into CRM-ready notes and ready-to-send follow-ups using conversation evidence, relationship context, and the writing patterns you already use.",
    indexable: true,
  },
  "/product/coaching": {
    title: "Evidence-Grounded Call Coaching | Callsect",
    socialTitle: "See what you could have said differently—and why",
    description:
      "Callsect grounds coaching in the actual customer moment, shows what you could have handled differently, and gives practical alternative wording for the next conversation.",
    indexable: true,
  },
  "/product/prepare-next-call": {
    title: "Prepare the Next Customer Call with Relationship Context | Callsect",
    socialTitle: "Walk into the next call knowing what matters",
    description:
      "Turn the previous customer conversation into a practical next-call plan with objectives, likely objections, questions, relationship context, and call-flow guidance.",
    indexable: true,
  },
  "/demo": {
    title: "Live Product Demo | Callsect",
    socialTitle: "Choose a Callsect demo for your customer calls",
    description:
      "Explore Callsect through a new-business or renewals and retention workflow, using the real product interface with synthetic customer-call data.",
    indexable: true,
  },
  "/demo/sales": {
    title: "Sales Call Demo | Callsect",
    socialTitle: "See Callsect follow a deal across conversations",
    description:
      "Follow a new-business opportunity from discovery through decision validation, coaching, CRM notes, relationship memory, and next-call preparation.",
    indexable: true,
  },
  "/demo/renewals": {
    title: "Renewals & Retention Demo | Callsect",
    socialTitle: "See Callsect carry a customer relationship forward",
    description:
      "Follow a difficult renewal through call analysis, coaching, CRM documentation, relationship memory, and next-call preparation.",
    indexable: true,
  },
  "/demo/sales/relationship": {
    title: "Sales Relationship Memory Demo | Callsect",
    socialTitle: "Deal context that compounds across calls",
    description:
      "Explore how Callsect preserves stakeholders, objections, commitments, and decision context across multiple sales conversations.",
    indexable: true,
  },
  "/demo/renewals/relationship": {
    title: "Relationship Memory Demo | Callsect",
    socialTitle: "Relationship memory that compounds across calls",
    description:
      "Explore how Callsect preserves the customer story, tracks what changed across calls, and prepares the next move.",
    indexable: true,
  },
  "/privacy": {
    title: "Privacy Notice | Callsect",
    socialTitle: "Callsect Privacy Notice",
    description:
      "How Callsect processes account data, temporary audio, transcripts, generated outputs, product events, and private-beta requests.",
    indexable: true,
  },
  "/terms": {
    title: "Private Beta Terms | Callsect",
    socialTitle: "Callsect Private Beta Terms",
    description: "The terms governing access to and use of the Callsect private beta.",
    indexable: true,
  },
  "/data-handling": {
    title: "Data Handling | Callsect",
    socialTitle: "How Callsect handles call data",
    description:
      "A plain-language overview of how Callsect processes audio, transcripts, generated outputs, retention, and deletion.",
    indexable: true,
  },
};

const PRIVATE_ROUTE_TITLES = [
  ["/dashboard", "Dashboard | Callsect"],
  ["/upload", "New Call | Callsect"],
  ["/import-transcript", "Import Transcript | Callsect"],
  ["/calls/", "Conversation | Callsect"],
  ["/customers/", "Customer Relationship | Callsect"],
  ["/customers", "Customers | Callsect"],
  ["/account", "Account | Callsect"],
  ["/profile", "Profile | Callsect"],
  ["/admin", "Admin | Callsect"],
  ["/prepare-next-call", "Prepare Next Call | Callsect"],
  ["/legal-consent", "Legal Consent | Callsect"],
  ["/login", "Sign In | Callsect"],
  ["/forgot-password", "Reset Password | Callsect"],
  ["/reset-password", "Choose a New Password | Callsect"],
  ["/verify-email", "Verify Email | Callsect"],
];

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLink(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function resolveRoute(pathname) {
  const publicMeta = PUBLIC_ROUTES[pathname];
  if (publicMeta) return publicMeta;

  const privateMatch = PRIVATE_ROUTE_TITLES.find(([prefix]) => (
    prefix.endsWith("/") ? pathname.startsWith(prefix) : pathname === prefix
  ));

  return {
    title: privateMatch?.[1] || "Callsect",
    socialTitle: "Callsect",
    description: "Private Callsect workspace.",
    indexable: false,
  };
}

function canonicalFor(pathname, indexable) {
  if (!indexable) return `${SITE_URL}/`;
  return pathname === "/" ? `${SITE_URL}/` : `${SITE_URL}${pathname}`;
}

function updateStructuredData(enabled) {
  const existing = document.getElementById("callsect-site-schema");
  if (!enabled) {
    existing?.remove();
    return;
  }

  const script = existing || document.createElement("script");
  script.id = "callsect-site-schema";
  script.type = "application/ld+json";
  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "Callsect",
        url: `${SITE_URL}/`,
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        name: "Callsect",
        url: `${SITE_URL}/`,
        description: DEFAULT_DESCRIPTION,
        publisher: { "@id": `${SITE_URL}/#organization` },
        inLanguage: "en",
      },
      {
        "@type": "SoftwareApplication",
        "@id": `${SITE_URL}/#software`,
        name: "Callsect",
        applicationCategory: "BusinessApplication",
        applicationSubCategory: "Relationship intelligence for customer calls",
        operatingSystem: "Web",
        url: `${SITE_URL}/`,
        image: SOCIAL_IMAGE,
        description: DEFAULT_DESCRIPTION,
        brand: { "@id": `${SITE_URL}/#organization` },
        audience: {
          "@type": "Audience",
          audienceType: "Professionals whose success depends on customer calls",
        },
        featureList: [
          "Relationship intelligence across customer calls",
          "AI call analysis and longitudinal relationship memory",
          "CRM-ready notes in the user's writing voice",
          "Evidence-grounded coaching",
          "Customer-calibrated next-call preparation",
          "Multilingual customer-call workflows",
        ],
      },
    ],
  });

  if (!existing) document.head.appendChild(script);
}

export default function SeoManager() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (typeof document === "undefined") return;

    const route = resolveRoute(pathname);
    const registrationMode = pathname === "/signup"
      || (pathname === "/login" && new URLSearchParams(search).get("mode") === "register");
    const pageTitle = registrationMode ? "Create Account | Callsect" : route.title;
    const canonicalUrl = canonicalFor(pathname, route.indexable);
    const robots = route.indexable
      ? "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
      : "noindex,nofollow,noarchive";

    document.title = pageTitle;

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: route.description,
    });
    upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    upsertMeta('meta[name="googlebot"]', { name: "googlebot", content: robots });

    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Callsect" });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_US" });
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: route.socialTitle || pageTitle,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: route.socialDescription || route.description,
    });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: SOCIAL_IMAGE });
    upsertMeta('meta[property="og:image:secure_url"]', {
      property: "og:image:secure_url",
      content: SOCIAL_IMAGE,
    });
    upsertMeta('meta[property="og:image:type"]', {
      property: "og:image:type",
      content: "image/png",
    });
    upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
    upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: "630" });
    upsertMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: CALLSECT_SOCIAL_IMAGE_ALT,
    });

    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: route.socialTitle || pageTitle,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: route.socialDescription || route.description,
    });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: SOCIAL_IMAGE });
    upsertMeta('meta[name="twitter:image:alt"]', {
      name: "twitter:image:alt",
      content: CALLSECT_SOCIAL_IMAGE_ALT,
    });

    upsertLink('link[rel="canonical"]', { rel: "canonical", href: canonicalUrl });
    updateStructuredData(Boolean(route.schema));
  }, [pathname, search]);

  return null;
}
